// Soft UI Dashboard PRO React components
import PropTypes from 'prop-types';

// Data
import { useQuery } from '@tanstack/react-query';
import { useAuth0 } from "@auth0/auth0-react";

// Components
import ClearwaysGroupsMap from "pages/components/Map/ClearwaysMaps/Groups";
import StopmonitorMap from "pages/components/Map/StopmonitorMapView";

// API/MODELS
import bksAPI from "services/api/bks";
import inspectionAPI from "modules/inspections/api";
import gtfsAPI from "services/api/gtfs";

// Mapbox
import mapboxgl from '!mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import 'mapbox-gl/dist/mapbox-gl.css';

// helper
import { useSoftUIController } from "hooks/context";
import { processDevicesData } from "utils/device";
import { useLocation } from "react-router-dom";

mapboxgl.accessToken = 'pk.eyJ1IjoiaGFycnltb250YW5vIiwiYSI6ImNqZWY0bDcyaTFkcGYzM28ydHFpaDJxaWcifQ.Wf7aNVePBRWZ9fMy_I81qw';

function MapView({ map, user }) {
    const [controller] = useSoftUIController();
    const { selectedOrg, userPermissions } = controller;

    bksAPI.loadToken(useAuth0());
    inspectionAPI.loadToken(useAuth0());
    gtfsAPI.loadToken(useAuth0());

    const location = useLocation();
    const selected_view_device = location?.state?.device && location?.state?.device?.organisation_id == selectedOrg ? location.state.device : undefined;

    const canOperationsView = userPermissions.includes("operations");
    const factoryAcceptanceTester = userPermissions.includes("operations:fat");

    const devices = useQuery({
        queryKey: ['getAllDevices', selectedOrg],
        queryFn: async () => await bksAPI.getAllDevices(),
        refetchInterval: 300 * 1000,
        refetchIntervalInBackground: true,
    });

    const maintenance_tags = useQuery({
        queryKey: ['getAllMaintenanceTags', selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllMaintenanceTags()
        }
    });

    const device_maintenance_tags = useQuery({
        queryKey: ['getAllDeviceMaintenanceTags', selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllDeviceMaintenanceTags()
        }
    });

    const all_inspections = useQuery({
        queryKey: ['getAllInspectionsForUser', user, selectedOrg],
        queryFn: async () => {
            return await inspectionAPI.getAllInspectionsForUser(user);
        }
    });

    const tags = useQuery({
        queryKey: ['getAllTags', selectedOrg],
        queryFn: async () => {
            return await gtfsAPI.getTags();
        }
    });

    if (devices.isLoading) {
        return ('Loading...');
    }

    if (devices.error || maintenance_tags.error || all_inspections.error || tags.error) {
        return ("An error has occurred: " + (devices?.error?.message || maintenance_tags?.error?.message || all_inspections?.error?.message || tags?.error?.message));
    }

    if (!devices.data) {
        return ('Loading...');
    }

    var concatted_array = [];

    if (canOperationsView) {
        const processed_maintenance_options = maintenance_tags?.data?.map(o => {
            return { ...o, category: 'Maintenance Work', value: o.tag_label, label: o.tag_label };
        }) || [];
        concatted_array.push(...processed_maintenance_options);
    }

    const processed_devices = processDevicesData(devices.dataUpdatedAt, devices.data, false, device_maintenance_tags.data);

    const processed_device_options = processed_devices?.map(o => {
        return { ...o, category: 'Devices', value: o.name, label: `${!(o.name.includes(o.serial_number)) ? (o.serial_number + " - ") : ""}${o.name} (${o.site_id})` }
    }) || [];
    concatted_array.push(...processed_device_options);

    const processed_tag_route_options = tags?.data?.routes?.map(o => {
        return { category: 'Routes', stop_ids: o.stop_ids, value: o.route_id, colour: o.route_color, rsn: o.route_short_name, label: `${o.route_short_name} - ${o.route_long_name} (${o.route_id.split('_')[0]})` }
    }).sort((a, b) => a.rsn.localeCompare(b.rsn, undefined, { numeric: true })) || [];
    concatted_array.push(...processed_tag_route_options);

    const combined_arrays = [... new Set(concatted_array)];

    const inspectionsData = all_inspections?.data?.inspections.filter(i => factoryAcceptanceTester || i.recordType == "inspection") || [];

    if (selectedOrg == 'clearways') {
        return <ClearwaysGroupsMap map={map} user={user} devices={devices} inspections={inspectionsData} selectedViewDevice={selected_view_device} />;
    }
    return <StopmonitorMap map={map} user={user} devices={processed_devices} inspections={inspectionsData} mapOptions={combined_arrays} selectedViewDevice={selected_view_device} />;
}

MapView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default MapView;